import type {ControlledDurationInputProps} from '@Components/controlled-duration-input/controlled-duration.input.types';
import type {KeyboardEvent, ReactElement} from 'react';
import React, {useEffect, useRef, useState, useCallback, useMemo} from 'react';
import {CONTROL_STATE, FOCUS_STATE} from '@Components/duration-input/duration-input.types';
import {Text, TextSize, TextType} from '@Components/text';
import {getUniqueString} from '@Utils/string.util';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import styles from './controlled-duration-input.module.scss';

const SECONDS_IN_MINUTE = 60;
const DELAY = 300;
const SECONDS_THRESHOLD = 59;
const STEP = 1;

export default function ControlledDurationInput({...props}: ControlledDurationInputProps): ReactElement<any> {
  const [focus, setFocus] = useState(FOCUS_STATE.SECONDS);
  const [minutes, setMinutes] = useState(Math.floor(props.duration / SECONDS_IN_MINUTE));
  const [seconds, setSeconds] = useState(props.duration % SECONDS_IN_MINUTE);
  const [isTyping, setIsTyping] = useState(false);
  const id = useRef(props.id ?? getUniqueString());
  const containerRef = useRef<HTMLDivElement>(null);
  const minuteInputRef = useRef<HTMLInputElement>(null);
  const secondInputRef = useRef<HTMLInputElement>(null);
  const controlUpRef = useRef<HTMLElement>(null);
  const controlDownRef = useRef<HTMLElement>(null);
  const prevSeconds = useRef(seconds);
  const prevMinutes = useRef(minutes);

  const format = (val: number): string => {
    return val < 10 ? `0${Math.floor(val)}` : `${Math.floor(val)}`;
  };

  const formattedSeconds = useMemo(() => {
    return format(seconds);
  }, [seconds]);

  const formattedMinutes = useMemo(() => {
    return format(minutes);
  }, [minutes]);

  const validate: (m: number, s: number) => boolean = useCallback(
    (m: number, s: number): boolean => {
      const combined = 60 * m + s;
      return props.minDuration <= combined && combined <= props.maxDuration;
    },
    [props.minDuration, props.maxDuration]
  );

  const onInputFocusForState = (state: FOCUS_STATE): void => {
    setFocus(state);
    if (state === FOCUS_STATE.MINUTES) {
      minuteInputRef.current?.select();
    }
    if (state === FOCUS_STATE.SECONDS) {
      secondInputRef.current?.select();
    }
  };

  const triggerControlUpKeyHighlight = (): void => {
    if (controlUpRef && controlUpRef.current) {
      controlUpRef.current?.classList.add(styles.clickEffect);
      setTimeout(() => {
        controlUpRef.current?.classList.remove(styles.clickEffect);
      }, 150);
    }
  };

  const triggerControlDownKeyHighlight = (): void => {
    if (controlDownRef && controlDownRef.current) {
      controlDownRef.current?.classList.add(styles.clickEffect);
      setTimeout(() => {
        controlDownRef.current?.classList.remove(styles.clickEffect);
      }, 150);
    }
  };

  const shouldDisableIncrement = (): boolean => {
    const incrementedMinutes = focus === FOCUS_STATE.MINUTES ? minutes + 1 : minutes;
    const incrementedSeconds = focus === FOCUS_STATE.SECONDS ? seconds + 1 : seconds;

    return !validate(incrementedMinutes, incrementedSeconds);
  };

  const shouldDisableDecrement = (): boolean => {
    const decrementedMinutes = focus === FOCUS_STATE.MINUTES ? minutes - 1 : minutes;
    const decrementedSeconds = focus === FOCUS_STATE.SECONDS ? seconds - 1 : seconds;

    return !validate(decrementedMinutes, decrementedSeconds);
  };

  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'ArrowUp' && !shouldDisableIncrement()) {
      triggerControlUpKeyHighlight();
    } else if (e.key === 'ArrowDown' && !shouldDisableDecrement()) {
      triggerControlDownKeyHighlight();
    }
  };

  const getDurationTextContainer = (): ReactElement<any> => {
    return (
      <div className={`${styles.durationTextContainer} flexbox flex-items-center spacing-p-l-3 spacing-p-r-3 hover-transition-all`}>
        <input
          onFocus={onInputFocusForState.bind(null, FOCUS_STATE.MINUTES)}
          className={`${styles.input} spacing-m-r-1 _borderless hover-transition-all`}
          ref={minuteInputRef}
          onKeyDown={onKeyDown}
          type="number"
          id={`${id.current}-minutes`}
          name="minutes"
          value={formattedMinutes}
          onChange={(e) => {
            setIsTyping(true);
            setMinutes(Number(e.target.value));
          }}
        />
        <Text val=":" className={`${styles.durationColon}`} type={TextType.BODY} size={TextSize.XSMALL} />
        <input
          onFocus={onInputFocusForState.bind(null, FOCUS_STATE.SECONDS)}
          className={`${styles.input} ${styles.secondsInput} spacing-m-l-1 _borderless hover-transition-all`}
          ref={secondInputRef}
          onKeyDown={onKeyDown}
          type="number"
          id={`${id.current}-seconds`}
          name="seconds"
          value={formattedSeconds}
          onChange={(e) => {
            setIsTyping(true);
            setSeconds(Number(e.target.value));
          }}
        />
      </div>
    );
  };

  const handleSecondsChange = (newSeconds: number): void => {
    const currentSeconds = seconds;

    if (currentSeconds > SECONDS_THRESHOLD) {
      setSeconds(prevSeconds.current);
      return;
    }

    if (!validate(minutes, newSeconds)) {
      setSeconds(prevSeconds.current);
      return;
    }

    prevSeconds.current = currentSeconds;
    props.onDurationChanged(60 * minutes + newSeconds);
  };

  const handleMinutesChange = (newMinutes: number): void => {
    const currentMinutes = minutes;

    if (currentMinutes > 59) {
      setMinutes(prevMinutes.current);
      return;
    }

    if (!validate(newMinutes, seconds)) {
      setMinutes(prevMinutes.current);
      return;
    }

    prevMinutes.current = currentMinutes;
    props.onDurationChanged(60 * newMinutes + seconds);
  };

  const getDisabledClassesForIcon = (): string => {
    return `${styles.iconDisabled} ${styles.disabled}`;
  };

  const onDurationControl = (control: CONTROL_STATE): void => {
    if (focus === FOCUS_STATE.SECONDS) {
      if (control === CONTROL_STATE.UP && !shouldDisableIncrement()) {
        handleSecondsChange(seconds + STEP);
      } else if (control === CONTROL_STATE.DOWN && !shouldDisableDecrement()) {
        handleSecondsChange(seconds - STEP);
      }
      secondInputRef.current?.select();
    }
    if (focus === FOCUS_STATE.MINUTES) {
      if (control === CONTROL_STATE.UP && !shouldDisableIncrement()) {
        handleMinutesChange(minutes + STEP);
      } else if (control === CONTROL_STATE.DOWN && !shouldDisableDecrement()) {
        handleMinutesChange(minutes - STEP);
      }

      minuteInputRef.current?.select();
    }
  };

  const getDurationControlsContainer = (): ReactElement<any> => {
    return (
      <div className={`flex-v-row ${styles.durationControlsContainer}`}>
        <Icon
          className={`${styles.durationControlUp} ${styles.durationControl} ${shouldDisableIncrement() ? getDisabledClassesForIcon() : ''}`}
          icon="icon-caret-up"
          size={IconSize.SIZE_ICON_16}
          onClick={(): void => {
            if (!shouldDisableIncrement()) {
              onDurationControl(CONTROL_STATE.UP);
            }
          }}
          isDisabled={shouldDisableIncrement()}
          ref={controlUpRef}
        />
        <Icon
          className={`${styles.durationControlDown} ${styles.durationControl} ${shouldDisableDecrement() ? getDisabledClassesForIcon() : ''}`}
          icon="icon-caret-down"
          size={IconSize.SIZE_ICON_16}
          onClick={(): void => {
            if (!shouldDisableDecrement()) {
              onDurationControl(CONTROL_STATE.DOWN);
            }
          }}
          isDisabled={shouldDisableDecrement()}
          ref={controlDownRef}
        />
      </div>
    );
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (isTyping) {
        if (focus === FOCUS_STATE.MINUTES) {
          handleMinutesChange(minutes);
        } else {
          handleSecondsChange(seconds);
        }
        setIsTyping(false);
      }
    }, DELAY);

    return () => {
      return clearTimeout(timeoutId);
    };
  }, [seconds, minutes, isTyping, props.minDuration, props.maxDuration, props.onDurationChanged.bind(null)]);

  useEffect(() => {
    setSeconds(props.duration % SECONDS_IN_MINUTE);
    setMinutes(Math.floor(props.duration / SECONDS_IN_MINUTE));
  }, [props.duration]);

  return (
    <div id={id.current} ref={containerRef} className={`flexbox border-s-darker radius-4 ${styles.container} ${props.className ?? ''}`}>
      {getDurationTextContainer()}
      {getDurationControlsContainer()}
    </div>
  );
}
